import { useState, useEffect, useRef, useCallback } from 'react';
import { GAME_MODES, CHARACTERS, SPECIAL_BUBBLES, ACHIEVEMENTS } from '../utils/gameData';
import { audioManager } from '../utils/audioManager';

export const useGameEngine = (mode = GAME_MODES.TIME_CHALLENGE) => {
  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [level, setLevel] = useState(1);
  const [bubbles, setBubbles] = useState([]);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [stats, setStats] = useState(() => {
    const savedAchievements = localStorage.getItem('gameAchievements');
    const savedHighScore = localStorage.getItem('gameHighScore');
    return {
      totalPopped: 0,
      maxCombo: 0,
      level: 1,
      speedCount: 0,
      lastPopTime: 0,
      highScore: savedHighScore ? parseInt(savedHighScore) : 0,
      specialBubbles: {
        gold: 0,
        time: 0,
        bomb: 0,
        combo: 0
      },
      achievements: savedAchievements ? JSON.parse(savedAchievements) : {}
    };
  });

  const [powerups, setPowerups] = useState({
    combo: { active: false, multiplier: 2 },
    time: { active: false }
  });

  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const timerRef = useRef(null);
  const spawnerRef = useRef(null);
  const comboTimerRef = useRef(null);
  const bubbleIdCounter = useRef(0);
  const maxBubbles = useRef(8);
  const powerupTimerRef = useRef(null);

  const LEVEL_THRESHOLDS = [0, 100, 350, 750, 1450];

  const calculateLevel = (currentScore) => {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (currentScore >= LEVEL_THRESHOLDS[i]) {
        return i + 1;
      }
    }
    return 1;
  };

  // 保存成就到 localStorage
  useEffect(() => {
    if (Object.keys(stats.achievements).length > 0) {
      try {
        localStorage.setItem('gameAchievements', JSON.stringify(stats.achievements));
      } catch (error) {
        console.error('Failed to save achievements:', error);
      }
    }
  }, [stats.achievements]);

  // 保存最高分到 localStorage
  useEffect(() => {
    if (stats.highScore > 0) {
      try {
        localStorage.setItem('gameHighScore', stats.highScore.toString());
      } catch (error) {
        console.error('Failed to save high score:', error);
      }
    }
  }, [stats.highScore]);

  const startGame = () => {
    setScore(0);
    setLevel(1);
    setBubbles([]);
    setCombo(0);
    setMaxCombo(0);
    setTimeLeft(mode === GAME_MODES.ENDLESS ? 0 : 60);

    const savedAchievements = localStorage.getItem('gameAchievements');
    const savedHighScore = localStorage.getItem('gameHighScore');

    setStats({
      totalPopped: 0,
      maxCombo: 0,
      level: 1,
      speedCount: 0,
      lastPopTime: 0,
      highScore: savedHighScore ? parseInt(savedHighScore) : 0,
      specialBubbles: {
        gold: 0,
        time: 0,
        bomb: 0,
        combo: 0
      },
      achievements: savedAchievements ? JSON.parse(savedAchievements) : {}
    });

    setPowerups({
      combo: { active: false, multiplier: 2 },
      time: { active: false }
    });

    setUnlockedAchievements([]);
    setNotifications([]);
    setGameState('playing');
    maxBubbles.current = 8;

    audioManager.init();
    audioManager.playStart();
  };

  const pauseGame = () => {
    setGameState(prev => (prev === 'playing' ? 'paused' : prev));
  };

  const resumeGame = () => {
    setGameState(prev => (prev === 'paused' ? 'playing' : prev));
    audioManager.init();
  };

  const endGame = useCallback(() => {
    setGameState('ended');
    audioManager.playGameOver();

    // 更新最高分
    setStats(prev => ({
      ...prev,
      highScore: Math.max(prev.highScore, score)
    }));

    // 清理所有計時器
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (spawnerRef.current) {
      clearInterval(spawnerRef.current);
      spawnerRef.current = null;
    }
    if (comboTimerRef.current) {
      clearTimeout(comboTimerRef.current);
      comboTimerRef.current = null;
    }
    if (powerupTimerRef.current) {
      clearTimeout(powerupTimerRef.current);
      powerupTimerRef.current = null;
    }
  }, [score]);

  const addNotification = (type, data = {}) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, type, ...data }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // 時間倒數
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (gameState === 'playing') {
      timerRef.current = setInterval(() => {
        if (mode === GAME_MODES.ENDLESS) {
          setTimeLeft(t => t + 1);
        } else {
          setTimeLeft(t => {
            const newTime = t - 1;
            if (newTime <= 0) {
              setGameState('ended');
              return 0;
            }
            return newTime;
          });
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState, mode]);

  useEffect(() => {
    if (gameState === 'ended') {
      endGame();
    }
  }, [gameState, endGame]);

  useEffect(() => {
    maxBubbles.current = Math.min(8 + (level - 1) * 4, 24);
  }, [level]);

  const spawnBubble = useCallback(() => {
    setBubbles(prev => {
      if (prev.length >= maxBubbles.current) {
        return prev;
      }

      const isMobile = window.innerWidth < 768;
      const padding = isMobile ? 100 : 150;
      const maxX = window.innerWidth - padding * 2;
      const maxY = window.innerHeight - padding * 2;
      const baseSize = isMobile ? 70 : 90;
      const size = Math.max(50, baseSize - level * 2);

      let x, y, attempts = 0;
      let overlapping = true;

      while (overlapping && attempts < 15) {
        x = padding + Math.random() * maxX;
        y = padding + Math.random() * maxY;

        const tooCloseToCorner =
          (x < padding * 1.5 && y < padding * 1.5) ||
          (x > window.innerWidth - padding * 1.5 && y < padding * 1.5) ||
          (x < padding * 1.5 && y > window.innerHeight - padding * 1.5) ||
          (x > window.innerWidth - padding * 1.5 && y > window.innerHeight - padding * 1.5);

        if (tooCloseToCorner) {
          attempts++;
          continue;
        }

        overlapping = prev.some(bubble => {
          const dx = bubble.x - x;
          const dy = bubble.y - y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          return distance < (bubble.size + size) / 2 + 30;
        });

        attempts++;
      }

      if (attempts >= 15) return prev;

      const isSpecial = Math.random() < Math.min(0.05 + level * 0.005, 0.15);
      let bubbleData;

      if (isSpecial) {
        const types = Object.values(SPECIAL_BUBBLES);
        bubbleData = { ...types[Math.floor(Math.random() * types.length)], isSpecial: true };
      } else {
        bubbleData = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
      }

      const baseDuration = mode === GAME_MODES.PRECISION ? 3500 : 5000;
      const duration = Math.max(1500, baseDuration - (level - 1) * 200);

      const newBubble = {
        ...bubbleData,
        id: bubbleIdCounter.current++,
        x,
        y,
        size,
        duration,
        health: bubbleData.health || 1
      };

      return [...prev, newBubble];
    });
  }, [level, mode]);

  useEffect(() => {
    if (spawnerRef.current) {
      clearInterval(spawnerRef.current);
      spawnerRef.current = null;
    }

    if (gameState === 'playing') {
      const baseRate = 1200;
      const spawnRate = Math.max(150, baseRate - (level - 1) * 250);
      spawnerRef.current = setInterval(spawnBubble, spawnRate);
    }

    return () => {
      if (spawnerRef.current) {
        clearInterval(spawnerRef.current);
        spawnerRef.current = null;
      }
    };
  }, [gameState, level, spawnBubble]);

  const handlePop = (id, bubbleData) => {
    let points = bubbleData.points || 10;
    const comboMultiplier = Math.min(1 + combo * 0.15, 3);
    points = Math.round(points * comboMultiplier);

    if (powerups.combo.active) {
      points *= powerups.combo.multiplier;
    }

    const newScore = score + points;
    setScore(newScore);

    setStats(prev => {
      const currentTime = Date.now();
      let speedCount = prev.speedCount;

      if (currentTime - prev.lastPopTime <= 10000) {
        speedCount++;
      } else {
        speedCount = 1;
      }

      // 追蹤特殊泡泡
      const newSpecialBubbles = { ...prev.specialBubbles };
      if (bubbleData.isSpecial && bubbleData.type) {
        newSpecialBubbles[bubbleData.type] = (newSpecialBubbles[bubbleData.type] || 0) + 1;
      }

      return {
        ...prev,
        totalPopped: prev.totalPopped + 1,
        lastPopTime: currentTime,
        speedCount,
        specialBubbles: newSpecialBubbles,
        highScore: Math.max(prev.highScore, newScore)
      };
    });

    const newLevel = calculateLevel(newScore);
    if (newLevel > level && newLevel <= 5) {
      setLevel(newLevel);
      setStats(prev => ({ ...prev, level: newLevel }));
      audioManager.playLevelUp();
      addNotification('levelup', { level: newLevel });
    }

    const newCombo = combo + 1;
    setCombo(newCombo);

    if (newCombo > maxCombo) {
      setMaxCombo(newCombo);
      setStats(prev => ({ ...prev, maxCombo: newCombo }));
    }

    if (comboTimerRef.current) {
      clearTimeout(comboTimerRef.current);
    }
    comboTimerRef.current = setTimeout(() => setCombo(0), 1500);

    // 特殊泡泡效果
    if (bubbleData.type === 'time' && mode !== GAME_MODES.ENDLESS) {
      setTimeLeft(t => t + 5);
      setPowerups(prev => ({ ...prev, time: { active: true } }));
      addNotification('powerup', { type: 'time' });
      
      if (powerupTimerRef.current) {
        clearTimeout(powerupTimerRef.current);
      }
      powerupTimerRef.current = setTimeout(() => {
        setPowerups(prev => ({ ...prev, time: { active: false } }));
      }, 1000);
    }

    if (bubbleData.type === 'combo') {
      setPowerups(prev => ({ ...prev, combo: { ...prev.combo, active: true } }));
      addNotification('powerup', { type: 'combo' });
      
      if (powerupTimerRef.current) {
        clearTimeout(powerupTimerRef.current);
      }
      powerupTimerRef.current = setTimeout(() => {
        setPowerups(prev => ({ ...prev, combo: { ...prev.combo, active: false } }));
      }, 10000);
    }

    if (bubbleData.type === 'gold') {
      addNotification('powerup', { type: 'gold' });
    }

    if (bubbleData.type === 'bomb') {
      addNotification('powerup', { type: 'bomb' });
      audioManager.playExplode();
    }

    setBubbles(prev => prev.filter(b => b.id !== id));
  };

  const handleRemove = (id, poppedByPlayer) => {
    if (!poppedByPlayer) {
      if (mode === GAME_MODES.PRECISION) {
        setScore(s => Math.max(0, s - 5));
      }
      setCombo(0);
    }
    setBubbles(prev => prev.filter(b => b.id !== id));
  };

  const checkAchievements = useCallback(() => {
    const newUnlocked = [];

    ACHIEVEMENTS.forEach(achievement => {
      if (!stats.achievements[achievement.id]) {
        let unlocked = false;

        if (achievement.condition) {
          unlocked = achievement.condition({
            ...stats,
            level,
            maxCombo: stats.maxCombo
          });
        }

        if (unlocked) {
          newUnlocked.push(achievement);
          setStats(prev => ({
            ...prev,
            achievements: {
              ...prev.achievements,
              [achievement.id]: true
            }
          }));
          audioManager.playAchievement();
        }
      }
    });

    if (newUnlocked.length > 0) {
      setUnlockedAchievements(prev => [...prev, ...newUnlocked]);
    }
  }, [stats, level]);

  useEffect(() => {
    if (gameState === 'playing') {
      checkAchievements();
    }
  }, [gameState, stats.totalPopped, level, stats.maxCombo, stats.specialBubbles, checkAchievements]);

  // 清理所有計時器
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (spawnerRef.current) clearInterval(spawnerRef.current);
      if (comboTimerRef.current) clearTimeout(comboTimerRef.current);
      if (powerupTimerRef.current) clearTimeout(powerupTimerRef.current);
    };
  }, []);

  return {
    gameState,
    score,
    timeLeft,
    level,
    bubbles,
    combo,
    maxCombo: stats.maxCombo,
    stats,
    powerups,
    unlockedAchievements,
    notifications,
    startGame,
    pauseGame,
    resumeGame,
    endGame,
    handlePop,
    handleRemove,
    removeNotification
  };
};